/**
 * OutlookLabel.jsx
 * Displays the current outlook period as a chip with hexagonal background
 * Migration from front/src/components/OutlookLabel.js to React 19 + Modern architecture
 */
import React, { memo } from 'react';
import { Box, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectOutlook, selectOutlooks } from '../store/slices/outlookSlice';
import { selectCurrentEntity } from '../store/slices/navigationSlice';
import { formatDomain } from '../utils/entityHelpers';
import DomainShape from './DomainShape';
import { DOMAIN_PATHS } from './domain-paths.js';

/**
 * Outlook label component with chip and hexagonal background
 * @param {Object} props - Component props
 * @param {string} props.color - Chip color variant
 * @param {Object} props.sx - Additional styling
 * @param {Object} props...props - Additional props passed to Box
 * @returns {React.ReactElement|null} OutlookLabel component or null if invalid outlook
 */
const OutlookLabel = ({ color = 'primary', sx = {}, ...props }) => {
  const outlook = useSelector(selectOutlook);
  const outlooks = useSelector(selectOutlooks);
  const currentOutlook = outlooks[outlook.index];
  const entity = useSelector(selectCurrentEntity);
  const domainKey = formatDomain(entity?.domaine);
  const hasDomainPath = !!DOMAIN_PATHS[domainKey];

  if (!currentOutlook) {
    return null;
  }

  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'middle',
        printColorAdjust: 'exact',
        WebkitPrintColorAdjust: 'exact',
        '@media print': {
          fontSize: '1.2em',
        },
        ...sx,
      }}
      {...props}
    >
      <Chip
        color={color}
        label={currentOutlook.value}
        sx={{
          height: '1.7em',
          borderRadius: '0.85em',
          paddingRight: '2em',
          fontSize: '1em',
          fontWeight: 700,
          '& .MuiChip-label': {
            pr: 0,
            position: 'relative',
            zIndex: 2,
          },
          '@media print': {
            backgroundColor: `${color}.main`,
            color: 'white',
          },
        }}
      />

      <Box
        component="span"
        sx={{
          display: 'inline-block',
          verticalAlign: 'middle',
          position: 'absolute',
          top: '50%',
          right: '-0.4em',
          transform: 'translateY(-50%)',
        }}
      >
        <DomainShape
          domain={domainKey}
          sx={{ width: '2.2em', verticalAlign: 'middle' }}
        />
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            position: 'absolute',
            top: '52%',
            transform: 'translateY(-50%)',
            right: 0,
            left: hasDomainPath ? '27%' : 0,
            textAlign: 'center',
            fontSize: '0.45em',
            color: 'secondary.contrastText',
            printColorAdjust: 'exact',
            WebkitPrintColorAdjust: 'exact',
            bgcolor: '#02568daa',
            borderRadius: '50%',
          }}
        >
          {currentOutlook.rwl}
        </Box>
      </Box>
    </Box>
  );
};

OutlookLabel.displayName = 'OutlookLabel';

export default memo(OutlookLabel);
